// SAD-861: propose a waiver of several dates at once — the ones picked in the
// dates table, a whole period, or everything from today on. Each date becomes
// its own proposal (dual control, D111). SAD-866: a proposal carries a note
// and waits for the COR; their answer is recorded on Awaiting approval (in a
// batch), or here at once when the COR already agreed. A second person then
// approves, where "Approve all" takes them in one click.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraFormField, SpecraInlineBanner, SpecraSelect, SpecraText, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { apiErrorMessage } from '../../../../lib/api/apiErrorMessage';
import { useProposeWaiver, useUploadEvidenceFiles } from '../hooks';
import { cmDate, todayISO } from '../lib/format';
import { CmDrawer } from './CmDrawer';
import { CorAgreedShortcut, corAnswerProblem, EMPTY_COR_ANSWER } from './CorAnswer';
/** How many dates the drawer lists before "and N more". */ const LISTED = 8;
/**
 * The scopes a waiver can cover, in the order offered: the picked dates,
 * each period, every open date from today on, every open date.
 */ export function waiveScopes(waivable, selectedIds, groups, today = todayISO()) {
    const open = new Set(waivable.map((o)=>o.id));
    const out = [];
    const picked = selectedIds.filter((id)=>open.has(id));
    const plural = (n)=>`${n} ${n === 1 ? 'date' : 'dates'}`;
    if (picked.length > 0) out.push({
        value: 'selected',
        label: `The ${plural(picked.length)} you picked`,
        ids: picked
    });
    for (const g of groups){
        const ids = g.ids.filter((id)=>open.has(id));
        if (ids.length > 0) out.push({
            value: `group:${g.key}`,
            label: `${g.label} (${plural(ids.length)})`,
            ids
        });
    }
    const ahead = waivable.filter((o)=>!!o.dueOn && o.dueOn >= today).map((o)=>o.id);
    if (ahead.length > 0 && ahead.length < waivable.length) out.push({
        value: 'ahead',
        label: `Every open date from today on (${plural(ahead.length)})`,
        ids: ahead
    });
    // "Every open date" adds nothing when one period already holds them all.
    const wholeGroup = out.some((s)=>s.value.startsWith('group:') && s.ids.length === waivable.length);
    if (waivable.length > 0 && !wholeGroup) out.push({
        value: 'all',
        label: `Every open date (${plural(waivable.length)})`,
        ids: waivable.map((o)=>o.id)
    });
    return out;
}
export function WaiveDatesDrawer({ programId, title, waivable, selectedIds, groups, lk, onClose, onDone }) {
    const scopes = useMemo(()=>waiveScopes(waivable, selectedIds, groups), [
        waivable,
        selectedIds,
        groups
    ]);
    const [scope, setScope] = useState(scopes[0]?.value ?? '');
    const [note, setNote] = useState('');
    const [agreed, setAgreed] = useState(false);
    const [cor, setCor] = useState(EMPTY_COR_ANSWER);
    const evidence = useUploadEvidenceFiles(programId);
    const [progress, setProgress] = useState(null);
    const [failures, setFailures] = useState([]);
    const propose = useProposeWaiver(programId, {
        errors: 'inline'
    });
    const ids = new Set(scopes.find((s)=>s.value === scope)?.ids ?? []);
    const targets = waivable.filter((o)=>ids.has(o.id));
    const followed = targets.filter((o)=>o.dependents > 0 || o.event && !o.event.projected).length;
    const busy = progress !== null || evidence.isPending;
    const problem = targets.length === 0 ? 'There are no open dates to waive.' : agreed ? corAnswerProblem(cor) : !note.trim() ? 'Add a note: why you propose waiving them.' : null;
    const run = async ()=>{
        setFailures([]);
        let evidenceSet = {
            documentId: '',
            attachments: []
        };
        if (agreed && cor.files.length > 0) {
            try {
                evidenceSet = await evidence.mutateAsync(cor.files);
            } catch (err) {
                setFailures([
                    apiErrorMessage(err, 'Could not upload the document')
                ]);
                return;
            }
        }
        setProgress({
            done: 0,
            total: targets.length
        });
        const failed = [];
        // One at a time: each proposal locks the program, so parallel calls
        // would only queue, and an early failure stays readable.
        for (const [i, o] of targets.entries()){
            try {
                await propose.mutateAsync(agreed ? {
                    occurrenceId: o.id,
                    note: note.trim(),
                    waivedOn: cor.on,
                    reason: cor.reason.trim(),
                    waiverDocumentId: evidenceSet.documentId,
                    ...evidenceSet.attachments.length > 0 ? {
                        attachments: evidenceSet.attachments
                    } : {}
                } : {
                    occurrenceId: o.id,
                    note: note.trim()
                });
            } catch (err) {
                failed.push(`${cmDate(o.dueOn)}: ${apiErrorMessage(err, 'could not propose')}`);
            }
            setProgress({
                done: i + 1,
                total: targets.length
            });
        }
        setProgress(null);
        const ok = targets.length - failed.length;
        if (ok > 0) toast.success(agreed ? `${ok} ${ok === 1 ? 'waiver' : 'waivers'} recorded as agreed by the COR. A second person approves ${ok === 1 ? 'it' : 'them'} on Awaiting approval.` : `${ok} ${ok === 1 ? 'waiver' : 'waivers'} proposed. ${ok === 1 ? 'The date stays' : 'The dates stay'} owed until the COR agrees and a second person approves.`);
        if (failed.length > 0) setFailures(failed);
        else onDone();
    };
    return /*#__PURE__*/ _jsx(CmDrawer, {
        title: "Propose a waiver",
        description: `${title}: propose that the client excuse these owed dates. Nothing changes yet: each stays owed, and late if it is late, until the COR agrees and a second person approves.`,
        onClose: onClose,
        busy: busy,
        testId: Testids.cmDuty.waiveDrawer,
        footerProps: {
            confirmLabel: targets.length > 0 ? `Propose ${targets.length} ${targets.length === 1 ? 'waiver' : 'waivers'}` : 'Propose',
            onConfirm: ()=>void run(),
            confirmDisabled: !!problem || busy,
            loading: busy,
            onCancel: onClose,
            confirmProps: {
                'data-testid': Testids.cmDuty.waiveSave
            },
            leading: progress ? /*#__PURE__*/ _jsxs(SpecraText, {
                size: "caption",
                tone: "muted",
                children: [
                    "Proposing ",
                    progress.done,
                    " of ",
                    progress.total,
                    "…"
                ]
            }) : problem ? /*#__PURE__*/ _jsx(SpecraText, {
                size: "caption",
                tone: "muted",
                children: problem
            }) : undefined
        },
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-3",
            children: [
                scopes.length === 0 ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    tone: "info",
                    variant: "framed",
                    children: "Every date of this deliverable is already waived, closed or awaiting approval."
                }) : /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Which dates",
                    required: true,
                    children: /*#__PURE__*/ _jsx(SpecraSelect, {
                        value: scope,
                        onChange: (e)=>setScope(e.target.value),
                        disabled: busy,
                        "data-testid": Testids.cmDuty.waiveScope,
                        children: scopes.map((s)=>/*#__PURE__*/ _jsx("option", {
                                value: s.value,
                                children: s.label
                            }, s.value))
                    })
                }),
                targets.length > 0 ? /*#__PURE__*/ _jsxs("ul", {
                    className: "space-y-0.5 text-[length:var(--fs-ui-body)]",
                    "data-testid": Testids.cmDuty.waiveDates,
                    children: [
                        targets.slice(0, LISTED).map((o)=>/*#__PURE__*/ _jsxs("li", {
                                className: "flex gap-3",
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "tabular-nums",
                                        children: cmDate(o.dueOn)
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "text-muted-foreground",
                                        children: lk.teamName(o.workstreamId)
                                    })
                                ]
                            }, o.id)),
                        targets.length > LISTED ? /*#__PURE__*/ _jsxs("li", {
                            className: "text-muted-foreground",
                            children: [
                                "and ",
                                targets.length - LISTED,
                                " more"
                            ]
                        }) : null
                    ]
                }) : null,
                followed > 0 ? /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                    tone: "warning",
                    variant: "framed",
                    children: [
                        followed === 1 ? 'One of these dates is' : `${followed} of these dates are`,
                        " followed by other deliverables. Waiving leaves them with no firm date; if the government moved the date instead, propose an extension from the date itself."
                    ]
                }) : null,
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Note",
                    required: !agreed,
                    hint: "The same note goes on every date: why you propose it, for the conversation with the client.",
                    children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                        rows: 2,
                        value: note,
                        onChange: (e)=>setNote(e.target.value),
                        disabled: busy,
                        "data-testid": Testids.cmDuty.waiveNote
                    })
                }),
                /*#__PURE__*/ _jsx(CorAgreedShortcut, {
                    agreed: agreed,
                    onAgreed: setAgreed,
                    value: cor,
                    onChange: setCor,
                    disabled: busy,
                    testId: Testids.cmDuty.waiveCorAgreed,
                    onTestId: Testids.cmDuty.waiveWaivedOn,
                    reasonTestId: Testids.cmDuty.waiveReason
                }),
                failures.length > 0 ? /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                    tone: "danger",
                    variant: "framed",
                    role: "alert",
                    children: [
                        failures.length === 1 ? 'One date was' : `${failures.length} dates were`,
                        " not proposed:",
                        ' ',
                        failures.join('; ')
                    ]
                }) : null
            ]
        })
    });
}
